import { AxiosStatic, AxiosError, AxiosResponse } from "axios";
import { AnsweredQuestionWithId } from "@teamprevent/common/model";

import { Questionnaire, QuestionnaireData } from "@teamprevent/common/questionnaires/model";
import { ImmunisationDecision } from "@teamprevent/common/immunisation/models";
import { ServerModelWithDateStrings } from "@teamprevent/common/utils";
import * as moment from "moment";

import { AddTaskFormModel } from "../task/model";
import { AddNextTaskFormModel } from "../tasks/models";
import { ImmunisationCreateModel } from "../immunisations/model";

import {
    ImmunisationReviewResult,
    parseImmunisationReviewResult,
    FollowUpTaskModel,
    SaveImmunisationReviewModel,
    ImmunisationReviewAutoSaveModel,
    TasksData,
    ImmunisationTasksData
} from "./model";

export class ImmunisationReviewApi {
    private readonly apiUrl = "/api/immunisationReview";

    private axios: AxiosStatic;

    constructor(ax: AxiosStatic) {
        this.axios = ax;
    }

    public async loadImmunisationReview(ohCaseId: string, taskId: string): Promise<Questionnaire> {
        const response = await this.axios.get(`${this.apiUrl}/${ohCaseId}/${taskId}`);
        return response.data as Questionnaire;
    }

    public async submitImmunisationReview(
        ohCaseId: string,
        taskId: string,
        questions: AnsweredQuestionWithId[],
        tasks: FollowUpTaskModel[],
        immunisationDecisions: { immunisationTypeId: string; immunisationDecision: ImmunisationDecision }[],
        immunisationRecords: ImmunisationCreateModel[]): Promise<ImmunisationReviewResult> {

        const parsedTasksModel = tasks.map<ServerModelWithDateStrings<FollowUpTaskModel>>(m => {
            return {
                ...m,
                dueDate: m.dueDate?.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS)
            };
        });

        const response = await this.axios.post<
            ServerModelWithDateStrings<ImmunisationReviewResult>,
            AxiosResponse<ServerModelWithDateStrings<ImmunisationReviewResult>>,
            {
                form: QuestionnaireData;
                tasks: ServerModelWithDateStrings<FollowUpTaskModel>[];
                immunisationDecisions: { immunisationTypeId: string; immunisationDecision: ImmunisationDecision }[];
                immunisationRecords: ServerModelWithDateStrings<ImmunisationCreateModel>[];
            }
        >(
            `${this.apiUrl}/${ohCaseId}/${taskId}`,
            {
                form: { questions },
                tasks: parsedTasksModel,
                immunisationDecisions,
                immunisationRecords: immunisationRecords.map(parseImmunisationCreateModelToServerModel)
            });
        return parseImmunisationReviewResult(response.data);
    }

    public async saveImmunisationReview(
        ohCaseId: string,
        taskId: string,
        questions: AnsweredQuestionWithId[],
        tasks: { task: AddTaskFormModel; isValid: boolean }[],
        immunisationTasks: { taskModel: AddNextTaskFormModel; immunisationTypeId: string }[],
        immunisationRecords: { immunisationModel: ImmunisationCreateModel; immunisationTypeId: string }[],
        eTag?: string): Promise<ImmunisationReviewAutoSaveModel> {
        try {
            const response = await this.axios.post<ImmunisationReviewAutoSaveModel, AxiosResponse<ImmunisationReviewAutoSaveModel>, {
                questionsData: QuestionnaireData;
                tasksData: TasksData;
                immunisationTasksData: ImmunisationTasksData;
                immunisationRecordsData: {
                    immunisations: { immunisationModel: ServerModelWithDateStrings<ImmunisationCreateModel>; immunisationTypeId: string }[];
                };
                eTag?: string;
            }>(
                `${this.apiUrl}/${ohCaseId}/${taskId}/autosave`, {
                    questionsData: { questions },
                    tasksData: { tasks },
                    immunisationTasksData: { tasks: immunisationTasks },
                    immunisationRecordsData: {
                        immunisations: immunisationRecords.map(i => ({
                            ...i,
                            immunisationModel: parseImmunisationCreateModelToServerModel(i.immunisationModel)
                        }))
                    },
                    eTag
                });
            return response.data;
        } catch (ex) {
            const axiosError = ex as AxiosError;
            if (axiosError.response?.status === 400) {
                const error = { message: "Could not save as a newer version exists", status: 400 };
                throw error;
            } else {
                throw ex;
            }
        }
    }

    public async loadSavedImmunisationReview(ohCaseId: string, taskId: string): Promise<SaveImmunisationReviewModel> {
        const response = await this.axios.get(`${this.apiUrl}/${ohCaseId}/${taskId}/savedreview`);
        return response.data as SaveImmunisationReviewModel;
    }
}

const parseImmunisationCreateModelToServerModel = (model: ImmunisationCreateModel): ServerModelWithDateStrings<ImmunisationCreateModel> => ({
    ...model,
    dateCompleted: model.dateCompleted.format(moment.HTML5_FMT.DATE)
});
